/**
 * WebGPU engine adapter for modern GPU rendering
 */

import { Animation, type Scene, WebGPUEngine } from "@babylonjs/core";
import type { AnyEngine, EngineOptions } from "../types";
import type { IEngineAdapter } from "./IEngineAdapter";

/**
 * Adapter for WebGPU Engine
 * Provides next-gen GPU-based rendering for modern browsers
 */
export class WebGPUEngineAdapter implements IEngineAdapter {
  private _engine: WebGPUEngine | null = null;
  private _canvas: HTMLCanvasElement | null = null;
  private _isInitialized = false;

  /**
   * The underlying WebGPUEngine instance
   */
  get engine(): AnyEngine | null {
    return this._engine;
  }

  /**
   * The type of engine adapter
   */
  get type(): string {
    return "webgpu";
  }

  /**
   * Whether the engine is initialized
   */
  get isInitialized(): boolean {
    return this._isInitialized;
  }

  /**
   * Create and initialize the WebGPU Engine
   * @param options Engine creation options
   */
  async createEngine(options: EngineOptions): Promise<AnyEngine> {
    if (this._engine) {
      throw new Error("Engine already created");
    }

    if (!options.canvas) {
      throw new Error("Canvas is required for WebGPU engine");
    }

    // Check WebGPU support - must check both navigator.gpu and requestAdapter
    if (
      !("gpu" in navigator) ||
      !navigator.gpu ||
      !navigator.gpu.requestAdapter
    ) {
      throw new Error("WebGPU is not supported in this browser");
    }

    this._canvas = options.canvas;

    this._engine = new WebGPUEngine(this._canvas, {
      antialias: options.antialias !== false,
      enableAllFeatures: true,
      enableGPUDebugMarkers:
        options.webgpuOptions?.enableGPUDebugMarkers || false,
      powerPreference:
        options.webgpuOptions?.powerPreference || "high-performance",
      setMaximumLimits: true,
      stencil: options.stencil !== false,
      useHighPrecisionMatrix: options.useHighPrecisionMatrix ?? true,
    });

    // WebGPU requires async initialization
    await this._engine.initAsync();

    Animation.AllowMatricesInterpolation = true;

    // Enable automatic resizing by default
    if (typeof window !== "undefined") {
      window.addEventListener("resize", this._handleResize);
    }

    this._isInitialized = true;

    return this._engine;
  }

  /**
   * Handle window resize events
   */
  private _handleResize = (): void => {
    if (this._engine) {
      this._engine.resize();
    }
  };

  /**
   * Start the render loop
   * @param renderFunction Function to call on each frame
   */
  startRenderLoop(renderFunction: () => void): void {
    if (!this._engine) {
      throw new Error("Engine not created");
    }

    this._engine.runRenderLoop(renderFunction);
  }

  /**
   * Stop the render loop
   */
  stopRenderLoop(): void {
    if (!this._engine) {
      throw new Error("Engine not created");
    }

    this._engine.stopRenderLoop();
  }

  /**
   * Resize the engine
   * @param width New width (optional, uses canvas size if not provided)
   * @param height New height (optional, uses canvas size if not provided)
   */
  resize(width?: number, height?: number): void {
    if (!this._engine) {
      throw new Error("Engine not created");
    }

    if (width !== undefined && height !== undefined && this._canvas) {
      this._canvas.width = width;
      this._canvas.height = height;
    }

    this._engine.resize();
  }

  /**
   * Begin a frame
   */
  beginFrame(): void {
    if (!this._engine) {
      throw new Error("Engine not created");
    }
    this._engine.beginFrame();
  }

  /**
   * End a frame
   */
  endFrame(): void {
    if (!this._engine) {
      throw new Error("Engine not created");
    }
    this._engine.endFrame();
  }

  /**
   * Render a scene
   * @param scene The scene to render
   */
  renderScene(scene: Scene): void {
    if (!this._engine) {
      throw new Error("Engine not created");
    }

    scene.render();
  }

  /**
   * Dispose of the engine and clean up resources
   */
  dispose(): void {
    // Remove resize listener
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this._handleResize);
    }

    if (this._engine) {
      try {
        this._engine.dispose();
      } catch (error) {
        // Engine might be partially created if WebGPU is not supported
        console.warn("Error disposing WebGPU engine:", error);
      }
      this._engine = null;
    }

    this._canvas = null;
    this._isInitialized = false;
  }

  /**
   * Get the rendering canvas
   */
  getCanvas(): HTMLCanvasElement | null {
    return this._canvas;
  }

  /**
   * Get the current frames per second
   */
  getFps(): number {
    if (!this._engine) {
      return 0;
    }
    return this._engine.getFps();
  }

  /**
   * Get delta time between frames
   */
  getDeltaTime(): number {
    if (!this._engine) {
      return 0;
    }
    return this._engine.getDeltaTime();
  }

  /**
   * Check if the engine supports a feature
   * @param feature Feature name to check
   */
  supportsFeature(feature: string): boolean {
    if (!this._engine) {
      return false;
    }

    switch (feature) {
      case "webgpu":
        return true;
      case "webgl":
      case "webgl2":
        return false; // WebGPU doesn't use WebGL
      case "webxr":
        return "xr" in navigator;
      case "compute":
        return true; // WebGPU supports compute shaders
      case "testing":
      case "nullengine":
        return false;
      default:
        // WebGPU supports most modern features
        return true;
    }
  }
}
